
import { CIcon, Path } from '~/icons/types'
import { getScopedStyleData } from '~/utils/vue'
import { mergeData } from 'vue-functional-data-merge'
import { PropType, defineComponent } from '~/utils/nuxt3-migration'
import { CreateElement, RenderContext, VNode } from 'vue'

export default defineComponent({
  name: 'CIcon',
  functional: true,
  props: {
    icon: {
      type: Object as PropType<CIcon>,
      required: true
    },
    noColors: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: [String, Number],
      default: null
    },
    fixedSize: {
      type: Boolean,
      default: false
    }
  },
  render(
    createElement: CreateElement,
    { data, parent, props }: RenderContext
  ): VNode {
    if (!props.icon) {
      return createElement('span')
    }

    const icon: CIcon = props.icon

    function getPathFill(path: Path) {
      if (path.fill && icon.name === 'vehicle-rentals') {
        // force path.fill even if noColors is true
        return path.fill
      } else if (!path.fill || props.noColors) {
        return 'currentColor'
      }
      return path.fill
    }

    return createElement(
      'svg',
      mergeData(
        data,
        {
          attrs: {
            'aria-hidden': 'true',
            focusable: 'false',
            'data-prefix': 'ci',
            role: 'img',
            xmlns: 'http://www.w3.org/2000/svg',
            viewBox: `${icon.minX || 0} ${icon.minY || 0} ${icon.width} ${
              icon.height
            }`,
            fill: icon.fill
          },
          staticClass: `ci tw-inline-block ci-${icon.name} ci-${icon.type}${
            !props.noColors ? ' colorize' : ''
          }`,
          class: { 'fixed-size': props.size || props.fixedSize },
          style: {
            width: props.size ? `${props.size}px` : undefined,
            height: props.size ? `${props.size}px` : undefined
          }
        },
        getScopedStyleData(parent)
      ),
      [
        ...icon.paths.map(path =>
          createElement('path', {
            attrs: {
              fill: getPathFill(path),
              stroke: path.stroke,
              'stroke-width': path.strokeWidth,
              'stroke-linecap': path.strokeLinecap,
              'stroke-linejoin': path.strokeLinejoin,
              opacity: path.opacity || '1.00',
              d: path.d,
              'fill-rule': path.fillRule,
              'clip-rule': path.clipRule,
              class: path.class
            }
          })
        ),
        ...(icon.ellipses || []).map(ellipse =>
          createElement('ellipse', {
            attrs: {
              fill: 'currentColor',
              cx: ellipse.cx,
              cy: ellipse.cy,
              rx: ellipse.rx,
              ry: ellipse.ry
            }
          })
        ),
        ...(icon.circles || []).map(circle =>
          createElement('circle', {
            attrs: {
              fill: circle.fill || 'none',
              stroke: 'currentColor',
              cx: circle.cx,
              cy: circle.cy,
              r: circle.r
            }
          })
        ),
        ...(icon.rectangles || []).map(rect =>
          createElement('rect', {
            attrs: {
              fill: 'currentColor',
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
              rx: rect.rx,
              ry: rect.ry
            }
          })
        ),
        ...(icon.defs
          ? [
              createElement('defs', {
                domProps: {
                  innerHTML: icon.defs
                }
              })
            ]
          : [])
      ]
    )
  }
})
